import { useMemo, useRef, useState } from "react";
import { ArrowSquareOut, ArrowsClockwise, FileArrowUp, ShieldCheck } from "@phosphor-icons/react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { usePortfolio } from "@/context/ProfileContext";
import { api, errMsg } from "@/lib/api";
import { num, usd } from "@/lib/format";
import { normFund } from "@/lib/statementParser";
import { TSP_LOGIN_URL, tspHoldings, readStatement, lastSharesUpdate } from "@/lib/tspUpdate";
import { TspAutoSync } from "@/components/TspAutoSync";

const key = (s) => s.replace(/\s+/g, "-").toLowerCase();

function ShareRows({ rows, shares, setShares }) {
  return (
    <div className="space-y-2" data-testid="tsp-update-rows">
      {rows.map((h) => (
        <div key={h.symbol} className="grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_140px_110px] items-center gap-3">
          <span className="text-sm font-medium">{h.symbol} Fund<span className="block text-xs text-muted-foreground font-normal">now {num(h.units, 4)} shares · ${num(h.live_price, 4)}/share</span></span>
          <Input type="number" min={0} step="0.0001" className="font-mono-num w-36 sm:w-auto" value={shares[h.symbol] ?? ""} onChange={(e) => setShares({ ...shares, [h.symbol]: e.target.value })} data-testid={`tsp-update-shares-${key(h.symbol)}`} />
          <span className="hidden sm:block text-right text-sm font-mono-num text-muted-foreground">{usd((Number(shares[h.symbol]) || 0) * (h.live_price || 0))}</span>
        </div>
      ))}
    </div>
  );
}

function UpdateForm({ onDone }) {
  const { dash, loadDash, profile, setProfile } = usePortfolio();
  const rows = useMemo(() => tspHoldings(dash), [dash]);
  const [shares, setShares] = useState(() => Object.fromEntries(rows.map((h) => [h.symbol, h.units])));
  const [alloc, setAlloc] = useState(null);
  const [useAlloc, setUseAlloc] = useState(true);
  const [busy, setBusy] = useState(false);
  const [note, setNote] = useState("");
  const file = useRef(null);
  const upload = async (f) => {
    if (!f) return;
    try {
      const { data } = await api.get("/prices/latest");
      const prices = Object.fromEntries((data.prices || []).map((p) => [p.fund, p.price]));
      const r = await readStatement(f, prices);
      if (!r) { toast.error("Couldn't find fund shares or balances in that file"); return; }
      const bySym = Object.fromEntries(rows.map((h) => [normFund(h.symbol), h.symbol]));
      const extra = Object.keys(r.shares).filter((s) => !bySym[s]);
      setShares({ ...shares, ...Object.fromEntries(Object.entries(r.shares).filter(([s]) => bySym[s]).map(([s, u]) => [bySym[s], u])) });
      setAlloc(r.allocation);
      setNote(extra.length ? `Not updated (not in your holdings yet): ${extra.join(", ")}. Add them on the Accounts page.` : "");
      toast.success("Shares filled in from your statement. Review, then save.");
    } catch (e) { toast.error(`Could not read file: ${e.message}`); } finally { if (file.current) file.current.value = ""; }
  };
  const save = async () => {
    setBusy(true);
    try {
      const entries = Object.entries(shares).filter(([, v]) => v !== "" && v != null && !isNaN(Number(v)));
      if (!entries.length) { toast.error("Enter shares for at least one fund"); setBusy(false); return; }
      const body = { shares: Object.fromEntries(entries.map(([k, v]) => [k, Number(v)])), allocation: useAlloc && alloc ? alloc : null };
      const r = await api.put("/tsp/shares", body);
      if (body.allocation && profile) setProfile({ ...profile, allocation: body.allocation });
      await loadDash();
      toast.success(`TSP updated. Balance ${usd(r.data.totals.tsp)}`);
      onDone();
    } catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  if (!rows.length) return <p className="text-sm text-muted-foreground" data-testid="tsp-update-empty">You don't have any TSP funds yet. Add your funds on the Accounts page first, then use this to keep the shares current.</p>;
  return (
    <div className="space-y-5">
      <ShareRows rows={rows} shares={shares} setShares={setShares} />
      <div className="flex flex-wrap items-center gap-3 border-t border-border pt-4">
        <input ref={file} type="file" accept=".csv,.pdf,text/csv,application/pdf" className="hidden" onChange={(e) => upload(e.target.files?.[0])} data-testid="tsp-update-file-input" />
        <Button variant="outline" className="rounded-full" onClick={() => file.current?.click()} data-testid="tsp-update-upload-button"><FileArrowUp size={16} className="mr-1.5" />Fill from TSP statement (PDF or CSV)</Button>
        {alloc && (
          <label className="flex items-center gap-2 text-sm" data-testid="tsp-update-alloc">
            <Checkbox checked={useAlloc} onCheckedChange={(v) => setUseAlloc(!!v)} data-testid="tsp-update-alloc-checkbox" />
            Use this statement's fund mix as my allocation: {Object.entries(alloc).filter(([, v]) => v).map(([k, v]) => `${k} ${v}%`).join(" · ")}
          </label>
        )}
      </div>
      {note && <p className="text-xs text-amber-700 dark:text-amber-400" data-testid="tsp-update-note">{note}</p>}
      <Button className="rounded-full w-full sm:w-auto" onClick={save} disabled={busy} data-testid="tsp-update-save-button">{busy ? "Saving..." : "Save TSP shares"}</Button>
    </div>
  );
}

export function TspUpdateButton({ variant = "outline", size = "default", label = "Update from TSP.gov", testid = "tsp-update-button", className = "" }) {
  const { dash } = usePortfolio();
  const [open, setOpen] = useState(false);
  const last = lastSharesUpdate(dash);
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><Button variant={variant} size={size} className={`rounded-full ${className}`} data-testid={testid}><ArrowsClockwise size={16} className="mr-1.5" />{label}</Button></DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="tsp-update-dialog">
        <DialogHeader>
          <DialogTitle>Update your TSP shares</DialogTitle>
          <DialogDescription>Last updated {last ? last.toLocaleDateString() : "never"}. Shares are multiplied by each day's official price to keep your balance current.</DialogDescription>
        </DialogHeader>
        <TspAutoSync onClose={() => setOpen(false)} />
        <p className="text-sm font-semibold">Option 2: Check TSP.gov yourself and update here</p>
        <ol className="text-sm space-y-2 list-decimal pl-5">
          <li>Open TSP.gov and sign in (use their one-time code). Go to <em>Account balance</em> or download your latest statement.
            <div className="mt-2"><a href={TSP_LOGIN_URL} target="_blank" rel="noopener noreferrer"><Button size="sm" className="rounded-full" data-testid="tsp-update-open-tsp-button"><ArrowSquareOut size={14} className="mr-1.5" />Open TSP.gov login</Button></a></div>
          </li>
          <li>Come back here and type the shares for each fund, or upload the statement and we'll fill them in.</li>
        </ol>
        <p className="flex gap-2 text-xs text-muted-foreground"><ShieldCheck size={16} className="text-emerald-600 shrink-0" />PillarWealth never asks for or stores your TSP.gov password and can't make changes to your TSP account.</p>
        {open && <UpdateForm onDone={() => setOpen(false)} />}
      </DialogContent>
    </Dialog>
  );
}
